/*
  Wire types for the JIT control plane API.

  These mirror docs/04-data-model.md section F.3 and docs/03-protocol.md section E.5. The
  rules that shaped them:

  * Ids are UUIDv7 strings. No sequential integer is ever exposed in a URL or a response,
    so there is no numeric id type anywhere in here.
  * Timestamps are ISO 8601 UTC strings on the wire, named *_at in SQL and *At here. They
    are deliberately NOT Date: a Date in a query cache is a mutable object that
    serialises inconsistently. Parse at the edge of render, in src/lib/format.ts.
  * Nullable SQL columns are "| null", not optional. A column that exists and is null is a
    different fact from a field the server did not send, and collapsing the two hides bugs.
  * String enums are "as const" unions rather than TypeScript enum, so they erase cleanly
    under verbatimModuleSyntax and match the CHECK constraints one to one.
*/

export type Uuid = string;
/** ISO 8601 timestamp in UTC, for example 2026-09-25T14:03:11Z. */
export type IsoTimestamp = string;

/* ---------------------------------------------------------------- tenancy */

export const ORGANIZATION_STATUSES = ['active', 'suspended', 'archived'] as const;
export type OrganizationStatus = (typeof ORGANIZATION_STATUSES)[number];

export interface Organization {
  readonly id: Uuid;
  readonly tenantId: Uuid;
  readonly name: string;
  readonly slug: string;
  readonly externalRef: string | null;
  readonly status: OrganizationStatus;
  readonly createdAt: IsoTimestamp;
  readonly updatedAt: IsoTimestamp;
}

export interface Site {
  readonly id: Uuid;
  readonly organizationId: Uuid;
  readonly name: string;
  readonly addressLine1: string | null;
  readonly city: string | null;
  readonly region: string | null;
  readonly postalCode: string | null;
  /** ISO 3166-1 alpha-2, char(2) in the schema. */
  readonly countryCode: string | null;
  readonly timezone: string;
  readonly createdAt: IsoTimestamp;
  readonly updatedAt: IsoTimestamp;
}

export interface DeviceGroup {
  readonly id: Uuid;
  readonly organizationId: Uuid;
  /** null means an organization-wide group, per the schema comment. */
  readonly siteId: Uuid | null;
  readonly name: string;
  readonly description: string | null;
  readonly createdAt: IsoTimestamp;
  readonly updatedAt: IsoTimestamp;
}

/* ---------------------------------------------------------------- devices */

export const DEVICE_PLATFORMS = ['windows', 'macos', 'linux'] as const;
export type DevicePlatform = (typeof DEVICE_PLATFORMS)[number];

export const AGENT_CHANNELS = ['stable', 'beta', 'internal'] as const;
export type AgentChannel = (typeof AGENT_CHANNELS)[number];

export const DEVICE_STATUSES = ['pending', 'active', 'revoked', 'retired'] as const;
/** Enrollment lifecycle, not liveness. Liveness is DevicePresenceState. */
export type DeviceStatus = (typeof DEVICE_STATUSES)[number];

export const DEVICE_PRESENCE_STATES = [
  'online',
  'offline',
  'session_active',
  'rebooting',
  'update_required',
] as const;
export type DevicePresenceState = (typeof DEVICE_PRESENCE_STATES)[number];

export const USER_SESSION_STATES = ['active', 'locked', 'logged_out', 'rdp'] as const;
export type EndpointUserSessionState = (typeof USER_SESSION_STATES)[number];

export interface DevicePresence {
  readonly deviceId: Uuid;
  readonly organizationId: Uuid;
  readonly state: DevicePresenceState;
  readonly gatewayNode: string | null;
  readonly localIp: string | null;
  readonly publicIp: string | null;
  readonly loggedInUser: string | null;
  readonly userSessionState: EndpointUserSessionState | null;
  readonly lastSeenAt: IsoTimestamp | null;
  readonly lastBootAt: IsoTimestamp | null;
  readonly connectedSince: IsoTimestamp | null;
  readonly policyRevision: number | null;
  readonly updatedAt: IsoTimestamp;
}

export interface Device {
  readonly id: Uuid;
  readonly organizationId: Uuid;
  readonly siteId: Uuid | null;
  readonly deviceGroupId: Uuid | null;

  readonly hostname: string;
  readonly displayName: string | null;
  readonly machineGuid: string | null;

  readonly platform: DevicePlatform;
  readonly osProduct: string | null;
  readonly osVersion: string | null;
  readonly osBuild: string | null;
  readonly osArch: string | null;
  readonly domainOrWorkgroup: string | null;
  readonly manufacturer: string | null;
  readonly model: string | null;
  readonly serialNumber: string | null;

  readonly agentVersion: string | null;
  readonly agentChannel: AgentChannel;

  readonly status: DeviceStatus;
  readonly enrolledAt: IsoTimestamp | null;
  readonly revokedAt: IsoTimestamp | null;
  readonly revokedReason: string | null;

  readonly tags: readonly string[];
  readonly notes: string | null;

  readonly createdAt: IsoTimestamp;
  readonly updatedAt: IsoTimestamp;
}

/* ------------------------------------------------------- device inventory */

/*
  Hardware inventory.

  docs/04-data-model.md F.3 stores this as device_inventory.payload, a jsonb blob keyed by
  kind ('hardware','software','services','updates','security'). jsonb on the wire would be
  `unknown` here and every consumer would have to narrow it by hand, so the hardware kind is
  given a declared shape instead. The server is responsible for producing this shape from
  the blob; a field the agent did not collect is null rather than absent, matching the rule
  at the top of this file.
*/

export interface CpuInventory {
  readonly model: string;
  readonly physicalCores: number | null;
  readonly logicalCores: number | null;
  readonly baseClockMhz: number | null;
  readonly architecture: string | null;
}

export interface MemoryInventory {
  readonly totalBytes: number;
  /** Null when the agent reported capacity but not live usage. */
  readonly availableBytes: number | null;
  readonly slotsUsed: number | null;
  readonly slotsTotal: number | null;
}

export const DISK_MEDIA_TYPES = ['ssd', 'hdd', 'nvme', 'removable', 'unknown'] as const;
export type DiskMediaType = (typeof DISK_MEDIA_TYPES)[number];

export interface DiskInventory {
  /** Mount point or drive letter, for example C: or /var. */
  readonly mountPoint: string;
  readonly label: string | null;
  readonly fileSystem: string | null;
  readonly media: DiskMediaType;
  readonly totalBytes: number;
  readonly freeBytes: number;
  readonly encrypted: boolean | null;
}

export interface GpuInventory {
  readonly model: string;
  readonly vendor: string | null;
  readonly vramBytes: number | null;
  readonly driverVersion: string | null;
}

export interface MonitorInventory {
  readonly name: string;
  readonly widthPx: number | null;
  readonly heightPx: number | null;
  readonly refreshHz: number | null;
  readonly primary: boolean;
  /** Effective scaling percentage, so per-monitor DPI is visible before a session starts. */
  readonly scalingPercent: number | null;
}

export interface NetworkAdapterInventory {
  readonly name: string;
  readonly macAddress: string | null;
  readonly ipv4: readonly string[];
  readonly ipv6: readonly string[];
  readonly linkSpeedMbps: number | null;
  readonly up: boolean;
  readonly dhcp: boolean | null;
}

export interface DeviceHardware {
  readonly collectedAt: IsoTimestamp;
  readonly cpu: CpuInventory | null;
  readonly memory: MemoryInventory | null;
  readonly disks: readonly DiskInventory[];
  readonly gpus: readonly GpuInventory[];
  readonly monitors: readonly MonitorInventory[];
  readonly networkAdapters: readonly NetworkAdapterInventory[];
}

/**
 * Everything the device detail page renders, in one request.
 *
 * One request rather than five because the page is useless in pieces: a technician deciding
 * whether to connect reads presence, the logged-in user and the last session together, and
 * five independent queries would paint them in five different frames and let them disagree.
 * The Activity tab's three lists are separated by source, not by request.
 */
export interface DeviceDetail {
  readonly device: Device;
  readonly presence: DevicePresence | null;
  readonly organizationName: string;
  readonly siteName: string | null;
  readonly deviceGroupName: string | null;
  /** Null when the agent has never completed an inventory pass. */
  readonly hardware: DeviceHardware | null;
  readonly recentSessions: readonly RemoteSession[];
  /** Technician actions against this device: connects, reboots, file transfers. */
  readonly technicianActivity: readonly AuditEvent[];
  /** Administrative changes to the record itself: move, tag, revoke, enroll. */
  readonly administrativeActivity: readonly AuditEvent[];
}

/**
 * The device list row: devices joined to device_presence plus the organization and site
 * names the list has to display, so the table is one request. The schema keeps
 * organization_id on every tenant-scoped row precisely so this join is cheap, see
 * docs/04-data-model.md F.1.
 */
export interface DeviceListRow {
  readonly device: Device;
  readonly presence: DevicePresence | null;
  readonly organizationName: string;
  readonly siteName: string | null;
  readonly deviceGroupName: string | null;
  /** Display name of the technician on the most recent session, null if never. */
  readonly lastTechnicianName: string | null;
  readonly lastSessionAt: IsoTimestamp | null;
}

/* ---------------------------------------------------------------- sessions */

export const SESSION_MODES = ['unattended', 'quick_support'] as const;
export type SessionMode = (typeof SESSION_MODES)[number];

export const SESSION_STATUSES = [
  'pending',
  'waiting_consent',
  'active',
  'ended',
  'denied',
  'expired',
  'failed',
] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

export const SESSION_CAPABILITIES = ['view', 'control', 'files', 'elevate'] as const;
export type SessionCapability = (typeof SESSION_CAPABILITIES)[number];

export const RECORDING_POLICIES = ['never', 'optional', 'always'] as const;
export type RecordingPolicy = (typeof RECORDING_POLICIES)[number];

export const CONNECTION_TYPES = ['direct', 'relay'] as const;
export type ConnectionType = (typeof CONNECTION_TYPES)[number];

export interface RemoteSession {
  readonly id: Uuid;
  readonly organizationId: Uuid;
  readonly organizationName: string;
  readonly deviceId: Uuid | null;
  readonly deviceName: string | null;
  readonly mode: SessionMode;
  readonly status: SessionStatus;
  /** Cleartext only while the session is pending, per docs/04-data-model.md. Else null. */
  readonly joinCode: string | null;
  readonly customerName: string | null;
  readonly requestedCaps: readonly SessionCapability[];
  readonly grantedCaps: readonly SessionCapability[];
  readonly consentRequired: boolean;
  readonly consentGrantedAt: IsoTimestamp | null;
  readonly recordingPolicy: RecordingPolicy;
  readonly createdByName: string | null;
  readonly createdAt: IsoTimestamp;
  readonly startedAt: IsoTimestamp | null;
  readonly endedAt: IsoTimestamp | null;
  readonly endReason: string | null;
  readonly connectionType: ConnectionType | null;
  readonly bytesSent: number;
  readonly bytesReceived: number;
}

/** docs/03-protocol.md section E.5: the 201 body from POST /devices/{id}/connect. */
export interface IceServer {
  readonly urls: readonly string[];
  readonly username?: string;
  readonly credential?: string;
}

export interface ConnectSessionResponse {
  readonly sessionId: Uuid;
  readonly wsUrl: string;
  readonly iceServers: readonly IceServer[];
  readonly iceTransportPolicy: 'all' | 'relay';
  /**
   * 120 second TTL, aud = session:{id}, spent when the viewer WebSocket connects, and
   * unusable against the REST API. Never persisted anywhere.
   */
  readonly viewerToken: string;
  readonly verificationRequired: boolean;
}

/* ---------------------------------------------------------------- identity */

export const USER_STATUSES = ['invited', 'active', 'suspended', 'disabled'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/**
 * The flat permission catalog from docs/07-identity-rbac.md section I.2. Kept as a union
 * so a typo in a permission gate fails the typecheck rather than silently denying.
 */
export const PERMISSIONS = [
  'device.view',
  'device.connect',
  'device.fileTransfer',
  'device.reboot',
  'device.manage',
  'device.elevate',
  'organization.view',
  'organization.manage',
  'session.create',
  'session.view',
  'session.join',
  'session.transfer',
  'session.recordings',
  'session.recordings.delete',
  'support.view',
  'support.manage',
  'technician.view',
  'technician.manage',
  'role.manage',
  'policy.view',
  'policy.manage',
  'audit.view',
  'audit.export',
  'release.view',
  'release.manage',
  'apikey.manage',
  'platform.manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/**
 * Permissions marked [dangerous] in the catalog. Drives a second confirmation in the UI
 * and a step-up MFA assertion in the API. The UI treatment is advisory only: enforcement
 * is server side and the console is never consulted, see docs/07-identity-rbac.md I.3.
 */
export const DANGEROUS_PERMISSIONS: readonly Permission[] = [
  'device.fileTransfer',
  'device.reboot',
  'device.manage',
  'device.elevate',
  'organization.manage',
  'session.recordings',
  'session.recordings.delete',
  'technician.manage',
  'role.manage',
  'policy.manage',
  'release.manage',
  'apikey.manage',
  'platform.manage',
];

export const BUILT_IN_ROLE_KEYS = [
  'platform_owner',
  'platform_administrator',
  'msp_administrator',
  'technician',
  'limited_technician',
  'customer_administrator',
  'auditor',
] as const;
export type BuiltInRoleKey = (typeof BUILT_IN_ROLE_KEYS)[number];

export interface Role {
  readonly id: Uuid;
  /** null for a built-in system role, per the schema. */
  readonly tenantId: Uuid | null;
  readonly key: string;
  readonly name: string;
  readonly description: string | null;
  readonly isSystem: boolean;
  readonly permissions: readonly Permission[];
}

/**
 * A role assignment is always scoped to exactly one node, or to none, meaning
 * tenant-wide. The ck_role_assignments_single_scope constraint enforces that; this type
 * mirrors it as a discriminated union so an impossible assignment does not typecheck.
 */
export type RoleAssignmentScope =
  | { readonly kind: 'tenant' }
  | { readonly kind: 'organization'; readonly organizationId: Uuid; readonly label: string }
  | { readonly kind: 'site'; readonly siteId: Uuid; readonly label: string }
  | { readonly kind: 'device_group'; readonly deviceGroupId: Uuid; readonly label: string }
  | { readonly kind: 'device'; readonly deviceId: Uuid; readonly label: string };

export interface RoleAssignment {
  readonly id: Uuid;
  readonly userId: Uuid;
  readonly roleId: Uuid;
  readonly roleName: string;
  readonly scope: RoleAssignmentScope;
  /** Non-null means a time-boxed elevation. */
  readonly expiresAt: IsoTimestamp | null;
  readonly createdAt: IsoTimestamp;
}

export type MfaMethodType = 'totp' | 'webauthn' | 'recovery_code';

export interface Technician {
  readonly id: Uuid;
  readonly tenantId: Uuid;
  readonly email: string;
  readonly fullName: string;
  readonly status: UserStatus;
  readonly mfaEnforced: boolean;
  readonly mfaMethods: readonly MfaMethodType[];
  readonly lockedUntil: IsoTimestamp | null;
  readonly lastLoginAt: IsoTimestamp | null;
  readonly assignments: readonly RoleAssignment[];
  readonly createdAt: IsoTimestamp;
}

export type AuthMethod = 'pwd' | 'otp' | 'webauthn' | 'sso';

/**
 * The signed-in technician. Note what is not here: the access token. It lives in
 * src/lib/api/token-store.ts, in memory, and never touches this object or the query cache.
 */
export interface CurrentUser {
  readonly id: Uuid;
  readonly tenantId: Uuid;
  readonly tenantName: string;
  readonly email: string;
  readonly fullName: string;
  /**
   * Effective permissions resolved server side for this request, flattened across every
   * assignment. Used for UI affordances only. The API re-resolves per request, so
   * revoking a permission takes effect on the next call, not on the next token refresh.
   */
  readonly permissions: readonly Permission[];
  readonly roleNames: readonly string[];
  /** Which factors the current session used. The amr claim in the access JWT. */
  readonly authMethods: readonly AuthMethod[];
  /** When MFA was last satisfied. Step-up windows are measured from here. */
  readonly mfaSatisfiedAt: IsoTimestamp | null;
}

/* ---------------------------------------------------------------- audit */

export const AUDIT_SEVERITIES = ['info', 'notice', 'warning', 'critical'] as const;
export type AuditSeverity = (typeof AUDIT_SEVERITIES)[number];

export const AUDIT_OUTCOMES = ['success', 'denied', 'failure'] as const;
export type AuditOutcome = (typeof AUDIT_OUTCOMES)[number];

export interface AuditEvent {
  readonly id: Uuid;
  readonly occurredAt: IsoTimestamp;
  /** Dotted action name, for example session.start or device.revoke. */
  readonly action: string;
  readonly severity: AuditSeverity;
  readonly outcome: AuditOutcome;
  readonly actorName: string | null;
  readonly actorEmail: string | null;
  readonly actorIp: string | null;
  readonly organizationName: string | null;
  readonly targetType: string | null;
  readonly targetLabel: string | null;
  /** Permission the authorization layer evaluated, including on a denial. */
  readonly permission: Permission | null;
  readonly detail: string | null;
  /** Position in the append-only hash chain. jitctl audit verify walks these. */
  readonly chainIndex: number;
}

/* ---------------------------------------------------------------- dashboard */

export type AttentionReason =
  | 'update_required'
  | 'offline_extended'
  | 'enrollment_pending'
  | 'agent_outdated'
  | 'reboot_pending';

export interface DeviceAttentionItem {
  readonly deviceId: Uuid;
  readonly deviceName: string;
  readonly organizationName: string;
  readonly reason: AttentionReason;
  readonly detail: string;
  readonly since: IsoTimestamp;
}

export interface DashboardSummary {
  readonly devicesOnline: number;
  readonly devicesOffline: number;
  readonly devicesTotal: number;
  readonly sessionsActive: number;
  readonly sessionsToday: number;
  readonly organizationsActive: number;
  readonly needsAttention: readonly DeviceAttentionItem[];
  readonly recentActivity: readonly AuditEvent[];
  /** Presence counts by state, so the dashboard and the device list cannot disagree. */
  readonly presenceBreakdown: Readonly<Record<DevicePresenceState, number>>;
}

/* ---------------------------------------------------------------- collections */

export interface Page<T> {
  readonly items: readonly T[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

/* ---------------------------------------------------------------- write payloads */

/*
  Request bodies for the Phase 1 writes.

  These are separate types rather than Partial<Organization>, for two reasons. A Partial
  would let a caller send id, tenantId, createdAt or status on a create, none of which the
  client gets to choose. And the parent keys below are the whole point of the site and group
  payloads: the schema's ck constraint and the uq indexes are expressed here as required
  fields, so a form that has not resolved its parent cannot typecheck into a request.
*/

export interface OrganizationCreate {
  readonly name: string;
  readonly slug: string;
  readonly externalRef: string | null;
}

export interface OrganizationUpdate {
  readonly name: string;
  readonly slug: string;
  readonly externalRef: string | null;
  readonly status: OrganizationStatus;
}

export interface SiteCreate {
  /** A site belongs to exactly one organization. Not nullable, not changeable later. */
  readonly organizationId: Uuid;
  readonly name: string;
  readonly addressLine1: string | null;
  readonly city: string | null;
  readonly region: string | null;
  readonly postalCode: string | null;
  readonly countryCode: string | null;
  readonly timezone: string;
}

export type SiteUpdate = Omit<SiteCreate, 'organizationId'>;

export interface DeviceGroupCreate {
  readonly organizationId: Uuid;
  /**
   * null means an organization-wide group. When non-null the server checks that the site
   * belongs to organizationId, which is the inconsistency the group form must not allow.
   */
  readonly siteId: Uuid | null;
  readonly name: string;
  readonly description: string | null;
}

export type DeviceGroupUpdate = Omit<DeviceGroupCreate, 'organizationId'>;

/** Where a device sits in the hierarchy. Both parents move together or the record lies. */
export interface DevicePlacementUpdate {
  readonly organizationId: Uuid;
  readonly siteId: Uuid | null;
  readonly deviceGroupId: Uuid | null;
}

export interface DeviceTagsUpdate {
  readonly tags: readonly string[];
}

export interface DeviceRevokeRequest {
  /** Recorded on the devices row and in the audit entry. Required, not optional. */
  readonly reason: string;
}

export interface DeviceRebootRequest {
  readonly reason: string;
  /** Ask the agent to warn the logged-in user and wait, rather than rebooting at once. */
  readonly notifyUser: boolean;
}

/**
 * The scope half of a role assignment, in request form.
 *
 * RoleAssignmentScope above is the response shape and carries a display label the server
 * resolved. A request has no label to send, so this is the narrower mirror of it. It is
 * still a discriminated union, so an assignment naming two scope nodes does not typecheck,
 * which is the ck_role_assignments_single_scope constraint expressed in the client.
 */
export type RoleAssignmentScopeInput =
  | { readonly kind: 'tenant' }
  | { readonly kind: 'organization'; readonly organizationId: Uuid }
  | { readonly kind: 'site'; readonly siteId: Uuid }
  | { readonly kind: 'device_group'; readonly deviceGroupId: Uuid }
  | { readonly kind: 'device'; readonly deviceId: Uuid };

export interface TechnicianInvite {
  readonly email: string;
  readonly fullName: string;
  readonly roleId: Uuid;
  readonly scope: RoleAssignmentScopeInput;
  /** Non-null makes the grant a time-boxed elevation that lapses on its own. */
  readonly expiresAt: IsoTimestamp | null;
}

export interface TechnicianInviteResult {
  readonly technician: Technician;
  /**
   * When the invitation email will lapse. The invite link itself is never returned to the
   * console: it goes to the invitee's mailbox and nowhere else, so a technician who can
   * read this response cannot sign in as the person they invited.
   */
  readonly invitationExpiresAt: IsoTimestamp;
}

export interface RoleAssignmentCreate {
  readonly userId: Uuid;
  readonly roleId: Uuid;
  readonly scope: RoleAssignmentScopeInput;
  readonly expiresAt: IsoTimestamp | null;
}
